import { useCallback, useState } from 'react';
import useOnUpdate from '../../../../hooks/useOnUpdate';
import { toInterval } from '../../../../utils/date';

export interface IUiDocument {
  thumbnailUrl: string;
  title: string;
  type: string;
  url: string;
}

export interface IProductCertification {
  image: string;
  title: string;
  subtitle: string;
  description: string;
  licensenumber: string;
}

export interface IUIProduct {
  accessories: ISimilarArticle[];
  adjacentOptions?: IAdjacentOptions[];
  brand: IBrand;
  breadcrumbs: IBreadcrumb[];
  categorization: ICategorization;
  categoryId?: string;
  configurations?: IConfiguration[];
  description: string;
  descriptions: IProductDescription;
  distributionType?: DistributionType;
  documents: IUiDocument[];
  energyClass: string;
  energyDeclarationImageUrl?: string;
  environmentImages: IImage[];
  goodToKnow?: string;
  isActive?: boolean;
  productCertifications: IProductCertification[];
  generalCertifications: ICertification[];
  gtin?: string;
  longDescription: string;
  moderArtikelId?: string;
  name: string;
  productImages: IImage[];
  productType: string;
  richSpecifications: ISpecifications;
  sellType: SellType;
  serie: string;
  siblings: ISimilarArticle[];
  specifications: ArticleSpecification;
  url: string;
  usps: string[];
  videos: string[];
  measurementsImages: IImage[];
  flags: IFlag[];
  price: IPrice;
  designer: IDesigner;
  assemblyPriceGroup: string;
  review: IReview;
  efiText: string;
}

export interface IImage {
  url: string;
  altText?: string;
}

interface IProductImage extends IImage {
  sortOrder: number;
}

const buildAltText = (imageDescriptions: string[]) =>
  imageDescriptions.filter((description) => !!description).join(' - ');

const getImages = ({ images, media, name, description, specifications }: IArticle) => {
  const productImages: IProductImage[] =
    images?.map((url, sortOrder) => ({ sortOrder, url })) || [];

  const environmentImages: string[] = [];
  const videos: string[] = [];
  const measurementsImages: string[] = [];

  const productImageAltText = buildAltText([
    name,
    description?.short,
    specifications?.colorRange as string,
  ]);
  const environmentImageAltText = buildAltText([name, description?.short, 'inspiration']);

  const measurementsImageAltText = buildAltText([name, description?.short, 'ritning']);

  media.map(toInterval).forEach(({ mediaType, url, youTubeId }) => {
    if (mediaType === 'Produktbild' || mediaType === 'Alternativ produktbild') {
      productImages.push({
        sortOrder: mediaType === 'Produktbild' ? 0 : 1,
        url,
      });
    }

    if (
      mediaType === 'Miljöbild' ||
      mediaType === 'Installationsbild' ||
      mediaType === 'Detaljbild' ||
      mediaType === 'Produktbild Miljö'
    ) {
      environmentImages.push(url);
    }

    if (mediaType === 'Ritning') {
      measurementsImages.push(url);
    }

    if ((mediaType === 'Produktfilm' || mediaType === 'Monteringsfilm') && youTubeId) {
      videos.push(youTubeId);
    }
  });

  const limitEnvironmentImages = environmentImages.slice(0, 7);

  return {
    productImages: productImages
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(({ url }) => ({ url, altText: productImageAltText }) as IImage),
    environmentImages: limitEnvironmentImages.map(
      (url) => ({ url, altText: environmentImageAltText }) as IImage,
    ),
    measurementsImages: measurementsImages.map(
      (url) => ({ url, altText: measurementsImageAltText }) as IImage,
    ),
    videos,
  };
};

const getExtension = (url: string) => url?.split('.').pop()?.toUpperCase() || '?';

const getDocuments = (documents: IDocument[], cdnUrl: string) =>
  documents.reduce(function (filteredDocuments, document) {
    if (document.documentType !== 'Märkning') {
      filteredDocuments.push({
        thumbnailUrl: `${cdnUrl}/documents/${document.url}?width=60&height=80&crop=false&output=jpg`,
        title: document.title,
        type: `${document.documentType} (${getExtension(document.url)})`,
        url: `/dokument/${document.url}`,
      });
    }
    return filteredDocuments;
  }, [] as IUiDocument[]);

const getEnergyDeclarationImageUrl = (documents: IDocument[], cdnUrl: string) => {
  const url = documents.find((d) => d.documentType === 'Energimärkning etikett')?.url;
  return url ? `${cdnUrl}/documents/${url}` : undefined;
};

const getGeneralCertifications = ({ certifications }: IArticle) => {
  // Sort certifications by prio and return the 2 with the highest priority. Only 2 certifications can be visable in the gallery footer.
  // The prio is administrated in Mio Tools
  const sortedByPrio = certifications.sort(
    (a, b) => (a.prio != null ? a.prio : Infinity) - (b.prio != null ? b.prio : Infinity),
  );

  return sortedByPrio.slice(0, 2);
};

const getCertifications = ({ certifications }: IArticle): IProductCertification[] => {
  return certifications.flatMap((certification) => {
    if (certification.licenses.length > 0) {
      return certification.licenses.map((license) => ({
        image: license.image,
        title: certification.name,
        subtitle: certification.description,
        description: license.description,
        licensenumber: license.licenseNumber,
      }));
    } else {
      return [
        {
          image: certification.image,
          title: certification.name,
          subtitle: certification.description,
          description: certification.longDescription,
          licensenumber: '',
        },
      ];
    }
  });
};

const sortConfigurations = (a: IConfiguration, b: IConfiguration) => a.sort - b.sort;

const parseProduct = (product: IArticle, cdnUrl: string): IUIProduct => ({
  accessories: product.accessories,
  adjacentOptions: product.adjacentOptions,
  brand: product.brand,
  breadcrumbs: product.breadcrumbs,
  categorization: product.categorization || ({} as ICategorization),
  categoryId: product.categorization?.categoryLeafId,
  configurations: product.configurations
    ? [...product.configurations].sort(sortConfigurations)
    : undefined,
  description: product.description?.short,
  descriptions: product.description,
  distributionType: product.distributionType,
  documents: getDocuments(product.documents, cdnUrl),
  energyClass: product.specifications.energyClass as string,
  energyDeclarationImageUrl: getEnergyDeclarationImageUrl(product.documents, cdnUrl),
  goodToKnow: product.description?.goodToKnow,
  efiText: product.description.efiText,
  gtin: product.gtin,
  isActive: product.isActive,
  productCertifications: getCertifications(product),
  generalCertifications: getGeneralCertifications(product),
  longDescription: product.description.long,
  moderArtikelId: product.moderArtikelId,
  name: product.name,
  productType: product.productType,
  richSpecifications: product.richSpecifications,
  sellType: product.sellType,
  serie: product.serie,
  siblings: product.siblings,
  specifications: product.specifications,
  url: product.url,
  usps: product.usps,
  flags: product.flags,
  price: product.price,
  designer: product.designer,
  assemblyPriceGroup: product.assemblyPriceGroup,
  review: product.review,
  ...getImages(product),
});

export default (product: IArticle, cdnUrl: string) => {
  const [parsedProduct, setParsedProduct] = useState(() => parseProduct(product, cdnUrl));

  useOnUpdate(
    () => {
      setParsedProduct(parseProduct(product, cdnUrl));
    },
    undefined,
    [product, cdnUrl],
  );

  const updateParsedProduct = useCallback(
    (variant: IOptionResponse) => {
      const newProduct = {
        ...product,
        accessories: variant?.accessories,
        certifications: variant?.certifications,
        documents: variant?.documents,
        flags: variant?.flags,
        images: variant?.images,
        price: variant?.price,
        richSpecifications: variant.richSpecifications,
        specifications: variant?.specifications,
        siblings: variant?.siblings,
      };
      setParsedProduct(parseProduct(newProduct, cdnUrl));
    },
    [product],
  );

  return {
    parsedProduct,
    setParsedProduct,
    updateParsedProduct,
  };
};
